<template>
    <div :class="componentClasses">
        <card-normal without-padding>
            <flex-container id="authentication__container" size="full">
                <form-validation v-slot="{ bindings: loginBindings }">
                    <div class="auth-container">
                        <logo-heyloha-icon v-if="hasHeylohaBranding()" class="logo" />
                        <logo-icon v-else class="logo" />

                        <div class="authentication__welcome">
                            <h1>
                                Welkom
                            </h1>
                            <text-description>
                                Login op jouw account
                            </text-description>
                        </div>
                        <input-group v-model="form.email" :schema="string().email().required()">
                            <template #input>
                                <text-input
                                    class="text-input"
                                    placeholder="E-mail"
                                    @keyup.enter="login(loginBindings.isValid.value)"
                                />
                            </template>
                        </input-group>

                        <input-group v-model="form.password" :schema="string().required()">
                            <template #input>
                                <text-input
                                    class="text-input"
                                    password
                                    placeholder="Wachtwoord"
                                    @keyup.enter="login(loginBindings.isValid.value)"
                                />
                            </template>
                        </input-group>

                        <flex-container
                            justify-content="space-between"
                            align-items="center"
                            size="auto"
                            no-wrap
                        >
                            <flex-container size="auto">
                                <input-group v-if="false" v-model="form.remember">
                                    <template #input="{ bindings }">
                                        <flex-container
                                            align-items="center"
                                            :gap="Size.sm"
                                            size="auto"
                                        >
                                            <div class="checkbox">
                                                <checkbox-input label="Onthoud mij" />
                                            </div>
                                            <label :for="bindings.ids.input">
                                                Onthoud mij
                                            </label>
                                        </flex-container>
                                    </template>
                                </input-group>
                            </flex-container>
                            <flex-container v-if="false" size="auto">
                                <div class="authentication__forgot-password">
                                    <a href="/" class="authentication__password-forgotten"><text-decorator :size="Size.sm">Wachtwoord vergeten?</text-decorator></a>
                                </div>
                            </flex-container>
                        </flex-container>
                        <button-block
                            ref="submitButton"
                            :disabled="!loginBindings.isValid.value"
                            @loading="login(loginBindings.isValid.value)"
                        >
                            Login
                        </button-block>
                    </div>
                </form-validation>

                <flex-container>
                    <div
                        v-if="hasHeylohaBranding()"
                        class="banner-image"
                    >
                        <img src="@/assets/images/heyloha-banner-image.png" alt="AI chat image" />
                    </div>
                    <carousel-container
                        v-else
                        class="carousel__background"
                    >
                        <template #slides>
                            <carousel-slide
                                contain
                            >
                                <div class="authentication__visual">
                                    <img
                                        :src="FlowDisplay"
                                        alt="Flow-display"
                                        class="authentication__visual-image"
                                    >
                                    <div class="authentication__visual-description">
                                        <h2 class="carousel__header">
                                            Interactieve & visuele flowweergave
                                        </h2>
                                        <text-description :color="'white'">
                                            Verander stappen in je workflow snel en makkelijk.
                                        </text-description>
                                    </div>
                                </div>
                            </carousel-slide>
                            <carousel-slide
                                contain
                            >
                                <div class="authentication__visual">
                                    <img
                                        :src="FlowDashboard"
                                        alt="Flow-dashboard"
                                        class="authentication__visual-image"
                                    >
                                    <div class="authentication__visual-description">
                                        <h2 class="carousel__header">
                                            Flow Dashboard voor complete flowstatistieken
                                        </h2>
                                        <text-description :color="'white'">
                                            Monitor je flows en hun prestaties met realtime updates.
                                        </text-description>
                                    </div>
                                </div>
                            </carousel-slide>
                        </template>

                        <template #interaction>
                            <carousel-navigation-arrow
                                direction="left"
                                horizontal-position="left"
                            />
                            <carousel-navigation-arrow
                                direction="right"
                                horizontal-position="right"
                            />
                            <carousel-navigation-dots
                                horizontal-position="center"
                                inverted
                            />
                        </template>
                    </carousel-container>
                </flex-container>
            </flex-container>
        </card-normal>
    </div>
</template>

<script setup lang="ts">
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { computed, ref } from 'vue';
    import {
        TextInput,
        TextDescription,
        CheckboxInput,
        Size,
        CarouselContainer,
        CarouselSlide,
        CarouselNavigationArrow,
        CarouselNavigationDots,
        ButtonBlock,
        ButtonState,
        NotificationBlock,
        useNotify,
    } from '@keyvue/one-ui';
    import LogoIcon from '@/assets/icons/logo.svg?component';
    import LogoHeylohaIcon from '@/assets/icons/logo-heyloha.svg?component';
    import FlowDisplay from '@/assets/images/promos/flow-display.png';
    import FlowDashboard from '@/assets/images/promos/flow-dashboard.png';
    import { UserAPI } from '@keyvue/one-api-client-legacy';
    import { string } from 'yup';
    import { useRouter } from 'vue-router';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    const { hasHeylohaBranding } = useAppBranding();
    const { client } = useClient();
    const { notify } = useNotify();
    const router = useRouter();

    const submitButton = ref<typeof ButtonBlock | null>(null);

    const login = async (isValid: boolean) => {
        if (!isValid) {
            return;
        }

        if (submitButton.value && submitButton.value.state !== ButtonState.loading) {
            submitButton.value.setState(ButtonState.loading);
            return; // setting the state to loading calls login again
        }

        await UserAPI.logout();

        try {
            await UserAPI.login(
                form.value.email,
                form.value.password,
            );

            if (UserAPI.isAuthenticated) {
                await client.session.applyTokens(
                    UserAPI.accessToken,
                    UserAPI.refreshToken,
                );

                await client.session.loadSession();
            }

            if (submitButton.value) {
                submitButton.value.setState(ButtonState.completed);
            }
        } catch (error) {
            notify({
                component: NotificationBlock,
                props: {
                    title: 'Er kon met deze gegevens niet worden ingelogd',
                    text: 'Controleer je gebruikersnaam/wachtwoord en probeer het nogmaals',
                    color: 'red-500',
                },
                duration: 4000,
            });

            if (submitButton.value) {
                submitButton.value.setState(ButtonState.content);
            }

            return;
        }

        if (client.session.isLoggedIn) {
            const urlParams = new URLSearchParams(window.location.search);
            const redirect = urlParams.get('redirect') ?? '/';

            if (redirect.indexOf('http') === 0) {
                window.location.replace(redirect);
            } else {
                router.replace(redirect);
            }
        }
    };

    interface Form {
        email: string
        password: string
        remember: boolean
    }

    const form = ref<Form>({
        email: '',
        password: '',
        remember: false,
    });

    const componentClasses = computed(() => ({
        'authentication': true,
        'authentication--heyloha': hasHeylohaBranding(),
    }));
</script>

<style scoped lang="scss">
#authentication__container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: size(md);
    background-color: white;
    min-height: 500px;
    border-radius: var(--card--border-radius, size(xs));
}
.auth-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 15px;
    margin-left: 5vw;
    margin-right: 5vw;
    height: 70vh;
    min-height: 500px;
    :deep(.text-input) {
        height: 4rem;
        border-width: 0;
        box-shadow: 1px 1px 1px 2px rgba(10, 10, 10, 0.05);
    }
    :deep(.text-input:has(.text-input__input:focus-visible)){
        height: 4rem;
        border-width: 0;
    }
}
.input-height {
    height: 10px;
}
.authentication {
    padding: 15vh 10vw;

    &__forgot-password {
        text-align: right;
    }
    &__password-forgotten {
        color: var(--theme-colors-default--primary);
        text-decoration: none;
    }
    &__password-forgotten:hover {
        text-decoration: underline;
        color: var(--theme-colors-default--primary);
    }
    &__visual {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        position: relative;
        height: 100%;
        justify-content: center;
    }
    &__visual-image {
        height: 16vw;
    }
    &__visual-description {
        text-align: center;
        padding-top: 2rem;
    }
    &__welcome {
        padding-bottom: 20px;
        padding-top: 20px;
    }
}
.logo {
    align-self: flex-start;
    height: 60px;
}
.carousel{
    &__background {
        background: linear-gradient(160deg, #5756a1, #2c2b51);
        border-bottom-right-radius: var(--card--border-radius, size(xs));
        border-top-right-radius: var(--card--border-radius, size(xs));
    }
    &__header {
        color: color(brand-white);
    }
}

.authentication--heyloha .carousel__background {
    background: linear-gradient(160deg, #008AC5, #006A98);
}

.authentication--heyloha .banner-image {
    position: relative;
    height: 70vh;
    width: 100%;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
    overflow: hidden;

    & > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
}
</style>
